import type { SigninRedirectArgs } from 'oidc-client-ts'
import React from 'react'
import {
  ErrorContext,
  hasAuthParams,
  useAuth,
} from 'react-oidc-context'
import { useIdle } from '@pogokid/react-hooks'
import {
  clearAuthParams,
  hasRetriedExpiredSignin,
  isAuthenticationExpired,
  markExpiredSigninRetried,
  resetExpiredSigninRetry,
} from './signinCallbackError'

/**
 * @public
 */
export interface WithAuthenticationRequiredProps {
  /**
   * Show a message when redirected to the signin page.
   */
  OnRedirecting?: () => React.JSX.Element

  /**
   * Display an Error when authentication process raises one.
   * `retry` clears the failed sign in from the address bar and
   * starts a new one.
   */
  OnError: (props: {
    error: ErrorContext
    retry: () => void
  }) => React.JSX.Element

  /**
   * Allows executing logic before the user is redirected to the signin page.
   */
  onBeforeSignin?: () => Promise<void> | void

  /**
   * Pass additional signin redirect arguments.
   */
  signinRedirectArgs?: () => SigninRedirectArgs
}

/**
 * A public higher-order component to protect accessing not public content. When you wrap your components in this higher-order
 * component and an anonymous user visits your component, they will be redirected to the login page; after logging in, they
 * will return to the page from which they were redirected.
 *
 * @public
 */
export const withAuthenticationRequired = <P extends object>(
  Component: React.ComponentType<P>,
  options: WithAuthenticationRequiredProps
): React.FC<P> => {
  const {
    OnRedirecting = (): React.JSX.Element => <></>,
    OnError,
    onBeforeSignin,
    signinRedirectArgs,
  } = options
  const displayName = `withAuthenticationRequired(${
    Component.displayName || Component.name
  })`
  const C: React.FC<P> = (props) => {
    const auth = useAuth()
    const isUserIdle = useIdle()
    // The error we are signing in again to get past. It stays in
    // `auth` until the redirect leaves the page, so hide it; any
    // newer error (e.g. the redirect failing) is shown.
    const [retryingError, setRetryingError] =
      React.useState<ErrorContext>()

    const retry = React.useCallback(() => {
      setRetryingError(auth.error)
      // Otherwise the new sign in would come back to a URL that
      // still holds the old response
      clearAuthParams()
      void auth.signinRedirect(signinRedirectArgs?.())
    }, [auth])

    React.useEffect(() => {
      if (
        auth.error &&
        isAuthenticationExpired(auth.error) &&
        !hasRetriedExpiredSignin()
      ) {
        markExpiredSigninRetried()
        retry()
      }
    }, [auth.error, retry])

    React.useEffect(() => {
      if (auth.isAuthenticated) resetExpiredSigninRetry()
    }, [auth.isAuthenticated])

    React.useEffect(() => {
      if (
        hasAuthParams() ||
        auth.isLoading ||
        auth.error ||
        auth.activeNavigator ||
        (auth.isAuthenticated && !auth.user?.expired)
      ) {
        return
      }
      void (async (): Promise<void> => {
        if (onBeforeSignin) await onBeforeSignin()
        // A silent sign in only stays on the page when we hold a
        // refresh token. Without one it runs in a hidden iframe,
        // which an app that blocks framing (`X-Frame-Options`,
        // `Cross-Origin-Embedder-Policy`) can never complete: it
        // would hold up the sign in until the iframe times out
        // and then fail anyway. Go straight to the sign in page
        // instead; anyone with a live session at the identity
        // provider comes back without having to type anything.
        const user = auth.user?.refresh_token
          ? await auth.signinSilent()
          : null
        if (user === null) {
          await auth.signinRedirect(signinRedirectArgs?.())
        }
      })()
    }, [
      isUserIdle,
      auth.isLoading,
      auth.activeNavigator,
      auth.isAuthenticated,
      auth.user?.expired,
      auth,
    ])

    return auth.error && auth.error !== retryingError ? (
      OnError({ error: auth.error, retry })
    ) : auth.isAuthenticated ? (
      <Component {...props} />
    ) : (
      OnRedirecting()
    )
  }

  C.displayName = displayName

  return C
}
